'use client';

import { useState, useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { UserPanel } from '../../apps/web/src/components/UserPanel';
import { Icon } from './icon';

function buildTree(items: any[]) {
  const map = new Map(items.map((i) => [i.id, { ...i, children: [] }]));
  const roots: any[] = [];
  for (const node of map.values()) {
    if (node.parentId && map.has(node.parentId)) {
      map.get(node.parentId)!.children.push(node);
    } else {
      roots.push(node);
    }
  }
  return roots;
}

function NavNode({ item, pathname, depth = 0, collapsed, theme }: any) {
  const hasChildren = item.children?.length > 0;
  const isActive = pathname === item.href || pathname.startsWith(item.href + '/');
  const containsActive = hasChildren && item.children.some((c: any) =>
    pathname === c.href || pathname.startsWith(c.href + '/')
  );

  const [expanded, setExpanded] = useState(!!containsActive || isActive);

  useEffect(() => {
    if (containsActive) setExpanded(true);
  }, [pathname, containsActive]);

  const bgColor = containsActive ? (theme.sidebarBgColor || theme.surfaceColor) : 'transparent';
  const txtColor = containsActive ? (theme.sidebarTextColor || theme.primaryColor) : (theme.sidebarTextColor || theme.textColor);

  if (hasChildren) {
    return (
      <div>
        <button
          onClick={() => setExpanded((v) => !v)}
          title={collapsed ? item.label : undefined}
          className={`w-full flex items-center ${collapsed ? 'justify-center' : 'justify-between'} gap-2 px-3 py-2 rounded-md text-left transition-colors`}
          style={{
            backgroundColor: bgColor,
            color: txtColor,
            fontWeight: containsActive ? 600 : 500,
          }}
        >
          <span className="flex items-center gap-2">
            <Icon name={item.icon} style={{ color: item.iconColor || txtColor }} size={18} />
            {!collapsed && <span className="text-sm">{item.label}</span>}
          </span>
          {!collapsed && (
            <span className={`text-xs transition-transform ${expanded ? 'rotate-90' : ''}`}><Icon name="ChevronRight" size={14} className="transition-transform" style={{ transform: expanded ? "rotate(90deg)" : "rotate(0deg)" }} /></span>
          )}
        </button>

        {expanded && !collapsed && (
          <div className="mt-1 ml-4 border-l pl-1 space-y-1" style={{ borderColor: theme.primaryColor }}>
            {item.children.map((child: any) => (
              <NavNode key={child.id} item={child} pathname={pathname} depth={depth + 1} collapsed={collapsed} theme={theme} />
            ))}
          </div>
        )}
      </div>
    );
  }

  const nodeBgColor = isActive ? (theme.sidebarBgColor || theme.surfaceColor) : 'transparent';
  const nodeTxtColor = isActive ? (theme.sidebarTextColor || theme.primaryColor) : (theme.sidebarTextColor || theme.textColor);

  return (
    <a
      href={item.href}
      title={collapsed ? item.label : undefined}
      className={`flex items-center ${collapsed ? 'justify-center' : ''} gap-2 px-3 py-2 rounded-md transition-colors text-left`}
      style={{
        backgroundColor: nodeBgColor,
        color: nodeTxtColor,
        fontWeight: isActive ? 600 : 400,
        paddingLeft: `${12 + depth * 14}px`,
      }}
    >
      <Icon name={item.icon} style={{ color: item.iconColor || nodeTxtColor }} size={18} />
      {!collapsed && <span className="text-sm">{item.label}</span>}
    </a>
  );
}

export function AppLayout({ children, user, onSignOut, dbNavItems = [], tenantName = 'Workspace', theme }: any) {
  const pathname = usePathname();
  const tree = buildTree(dbNavItems);

  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userPanelOpen, setUserPanelOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(user);

  const currentTheme = theme || {
    primaryColor: '#2563eb',
    accentColor: '#3b82f6',
    backgroundColor: '#ffffff',
    surfaceColor: '#f9fafb',
    textColor: '#111827',
    sidebarBgColor: null,
    sidebarTextColor: null,
    topbarBgColor: null,
    topbarTextColor: null,
    borderRadius: 8,
    fontFamily: 'Inter',
    sidebarWidth: 256,
  };

  useEffect(() => {
    const saved = localStorage.getItem('sidebar-collapsed');
    if (saved === 'true') setCollapsed(true);
  }, []);

  const toggleCollapsed = () => {
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem('sidebar-collapsed', String(next));
  };

  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  const initials = (currentUser?.name || currentUser?.email || '?')
    .split(' ')
    .map((s: string) => s[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <div 
      className="flex h-screen w-full overflow-hidden" 
      style={{ 
        backgroundColor: currentTheme.backgroundColor, 
        color: currentTheme.textColor,
        backgroundImage: currentTheme.backgroundImage ? `url(${currentTheme.backgroundImage})` : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
        backgroundRepeat: 'no-repeat',
        fontFamily: currentTheme.fontFamily,
      }}
    >
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 md:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <aside
        className={`
          fixed md:relative z-40 h-full
          transition-all duration-300 ease-in-out
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
          ${collapsed ? 'w-16' : ''}
          flex flex-col
        `}
        style={{ 
          width: collapsed ? '64px' : `${currentTheme.sidebarWidth}px`,
          backgroundColor: currentTheme.sidebarBgColor || currentTheme.backgroundColor,
        }}
      >
        <div 
          className={`h-16 px-3 flex items-center ${collapsed ? 'justify-center' : 'justify-between'}`} 
        >
          <button 
            onClick={() => setUserPanelOpen(true)} 
            className="flex items-center gap-2 w-full hover:opacity-80 transition-opacity text-left"
            title="Open User Profile"
            style={{ color: currentTheme.sidebarTextColor || currentTheme.textColor }}
          >
            {currentUser?.image ? (
              <img src={currentUser.image} alt="" className="w-9 h-9 rounded-full object-cover flex-shrink-0" />
            ) : (
              <div className="w-9 h-9 rounded-full flex-shrink-0 flex items-center justify-center font-semibold text-sm text-white" style={{ backgroundColor: currentTheme.primaryColor }}>
                {initials}
              </div>
            )}
            {!collapsed && (
              <div className="text-left min-w-0">
                <div className="text-sm font-semibold leading-tight truncate max-w-[120px]">
                  {currentUser?.name || 'User'}
                </div>
                <div className="text-xs opacity-60 leading-tight truncate max-w-[120px]">
                  {currentUser?.email}
                </div>
              </div>
            )}
          </button>
        </div>

        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {tree.map((item) => (
            <NavNode key={item.id} item={item} pathname={pathname} collapsed={collapsed} theme={currentTheme} />
          ))}
        </nav>

        <button
          onClick={toggleCollapsed}
          className="hidden md:flex absolute -right-3 top-1/2 -translate-y-1/2 items-center justify-center w-6 h-6 rounded-full border shadow-sm hover:scale-110 transition-all z-50"
          style={{ 
            backgroundColor: currentTheme.surfaceColor || '#ffffff',
            borderColor: currentTheme.surfaceColor || '#e5e7eb',
            color: currentTheme.primaryColor
          }}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? (
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
            </svg>
          ) : (
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
            </svg>
          )}
        </button>
      </aside>

      <div className="flex flex-col flex-1 w-full min-w-0">
        <header 
          className="h-16 flex-shrink-0 flex items-center justify-between px-4 md:px-6" 
          style={{ 
            backgroundColor: currentTheme.topbarBgColor || currentTheme.backgroundColor, 
            color: currentTheme.topbarTextColor || currentTheme.textColor
          }}
        >
          <div className="flex items-center gap-3">
            <button
              onClick={() => setMobileOpen(true)}
              className="md:hidden p-2 rounded-md hover:opacity-80"
              aria-label="Open menu"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <h1 className="text-lg font-semibold">{tenantName}</h1>
          </div>

          <div className="flex items-center gap-3">
            {currentUser && onSignOut && (
              <form action={onSignOut}>
                <button
                  type="submit"
                  className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md hover:opacity-80 transition-opacity text-white"
                  style={{ borderRadius: `${currentTheme.borderRadius}px`, backgroundColor: currentTheme.primaryColor }}
                  title="Logout"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  <span>Logout</span>
                </button>
              </form>
            )}
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-4 md:p-6 w-full">
          {children}
        </main>
      </div>

      <UserPanel 
        isOpen={userPanelOpen} 
        onClose={() => setUserPanelOpen(false)} 
        user={currentUser} 
      />
    </div>
  );
}
